import React from 'react';
import styled, { css } from 'styled-components';

interface Props<T> {
  inputId?: string;
  name: string;
  value: T;
  checked: boolean;
  onChange(value: T): void;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const MyRadioButton = <T extends string>({
  inputId: id,
  name,
  value,
  checked,
  onChange,
  className,
  style,
  disabled,
}: Props<T>) => {
  return (
    <Radio $checked={checked} className={className} style={style}>
      <HiddenInput
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
      />
      <Box $checked={checked} />
    </Radio>
  );
};

export default MyRadioButton;

const Radio = styled.div<{ $checked: boolean }>`
  --radio-color: ${(props) => props.theme.colors.primaryOrange};
  width: 20px;
  height: 20px;
  position: relative;
  flex-shrink: 0;
  display: inline-flex;

  border-radius: 50%;
  border: 2.1px solid #ced4da;
  background: #ffffff;

  /* HACK: when decreasing width/height according to the design,
           there are some weird subpixel artifacts (Chrome, Windows).
           Looks fine when using scaling :( */
  transform: scale(0.8);

  &:has(input:focus-visible) {
    box-shadow: 0 0 4px var(--radio-color);
    border-color: var(--radio-color);
  }

  &:not(:has(input:disabled)):hover {
    border-color: var(--radio-color);
  }
  ${(props) =>
    props.$checked &&
    css`
      border-color: var(--radio-color);
    `}
`;

const HiddenInput = styled.input`
  appearance: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  opacity: 0;
  z-index: 1;
  outline: 0;
  border: 2px solid #ced4da;
  border-radius: 50%;

  &:not(:disabled) {
    cursor: pointer;
  }
`;

const Box = styled.div<{ $checked: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;

  &::after {
    content: '';
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transform: translateZ(0) scale(0.1);
    border-radius: 50%;
    visibility: hidden;
    width: 12px;
    height: 12px;
    transition-duration: 0.2s;
    background-color: #ffffff;
  }

  ${(props) =>
    props.$checked &&
    css`
      &::after {
        transform: translateZ(0) scale(1, 1);
        visibility: visible;
        background-color: var(--radio-color);
      }
    `}
`;
